import { ChevronSelectorVertical, LogOut01 } from '#app/components/icons/solid'
import { useRef } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useNavigate } from 'react-router'
import { AvatarLabelGroup } from '#app/components/base/avatar/avatar-label-group'
import { Dropdown } from '#app/components/base/dropdown/dropdown.tsx'
import { useLogout } from '#app/hooks/use-auth-mutations.ts'
import { useBreakpoint } from '#app/hooks/use-breakpoint'
import { cx } from '#app/utils/cx'
import { usePreserveNuqsParams } from '#app/utils/navigation.ts'

export const NavAccountCard = ({
	email,
	name,
}: {
	email: string
	name: string
}) => {
	const triggerRef = useRef<HTMLDivElement>(null)
	const { isAbove } = useBreakpoint()
	const isDesktop = isAbove('lg')
	const navigate = useNavigate()
	const buildUrl = usePreserveNuqsParams(['redirectTo'])
	const logoutMutation = useLogout({
		onSuccess: () => navigate(buildUrl('/')),
	})

	// Get user initials for avatar fallback
	const getInitials = (name: string) => {
		if (!name) return '?'
		const parts = name.split(' ')
		if (parts.length >= 2) {
			return (parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')
		}
		return name[0] || '?'
	}

	return (
		<div
			ref={triggerRef}
			className="ring-secondary relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-inset"
		>
			<AvatarLabelGroup
				size="md"
				title={name}
				subtitle={email}
				initials={getInitials(name)}
			/>

			<div className="absolute top-1.5 right-1.5">
				<Dropdown.Root>
					<AriaButton className="text-fg-quaternary outline-focus-ring hover:bg-primary_hover hover:text-fg-quaternary_hover pressed:bg-primary_hover pressed:text-fg-quaternary_hover flex cursor-pointer items-center justify-center rounded-md p-1.5 transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2">
						<ChevronSelectorVertical className="size-4 shrink-0" />
					</AriaButton>
					<Dropdown.Popover
						placement={isDesktop ? 'right bottom' : 'top right'}
						triggerRef={triggerRef}
						offset={8}
						className={({ isEntering, isExiting }) =>
							cx(
								'origin-(--trigger-anchor-point) will-change-transform',
								isEntering &&
									'animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5 duration-150 ease-out',
								isExiting &&
									'animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5 duration-100 ease-in',
							)
						}
					>
						<Dropdown.Menu>
							<Dropdown.Section>
								<Dropdown.Item
									icon={LogOut01}
									onAction={() => logoutMutation.mutate()}
									isDisabled={logoutMutation.isPending}
								>
									Sign out
								</Dropdown.Item>
							</Dropdown.Section>
						</Dropdown.Menu>
					</Dropdown.Popover>
				</Dropdown.Root>
			</div>
		</div>
	)
}
